import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  extractApiErrorMessage,
  MeterFlowVolumeSearchCriteria,
  meterFlowVolumesSelector,
  useSearchMeterFlowVolumesQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {MeterFlowVolumeDataTable} from "../../components/data-table/MeterFlowVolumeDataTable/MeterFlowVolumeDataTable";
import {FromToDateToolbar} from "../../components/toolbar/FromToDateToolbar/FromToDateToolbar";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIMeterFlowVolumeSearchFilter} from "../../store/entity/meter-flow-volume/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIMeterFlowVolumeSearchFilter} from "../../store/entity/meter-flow-volume/actions";
import {useNavigate} from "react-router-dom";
import {useMeterFlowVolumeMutation} from "../../hooks/useMeterFlowVolumeMutation";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {notifications} from "@mantine/notifications";

export type MeterFlowVolumeSearchPageProps = {}

export const MeterFlowVolumeSearchPage = (props: MeterFlowVolumeSearchPageProps) => {
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const criteria = useOtsUISelector(selectUIMeterFlowVolumeSearchFilter)

  const selectMeterFlowVolumes = useMemo(() => {
    return meterFlowVolumesSelector();
  }, []);

  const searchArg: MeterFlowVolumeSearchCriteria | typeof skipToken = useMemo(() => {
    if (criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionIds: regionId ? [regionId] : undefined,
        categories: appCategory ? [appCategory.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory])

  const {meterFlowVolumes, isFetching, error, refetch} = useSearchMeterFlowVolumesQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      meterFlowVolumes: selectMeterFlowVolumes(result, {regionId: regionId}),
      isFetching: result.isFetching,
      error: result.error,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    if (fromDate.getTime() === criteria?.fromDttm && toDate?.getTime() === criteria.toDttm) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIMeterFlowVolumeSearchFilter({
        ...criteria,
        fromDttm: fromDate.getTime(),
        toDttm: toDate?.getTime()
      }))
    }
  }, [criteria, refetch, dispatch]);

  const {
    deleteMeterFlowVolume,
  } = useMeterFlowVolumeMutation()

  if (error) {
    notifications.show({
      id: "meterFlowVolumeSearchError",
      title: t("common.error", "Error"),
      message: extractApiErrorMessage(error) ?? t("common.error", "Error"),
      color: "red",
      loading: false,
      autoClose: true,
    });
  } else {
    notifications.hide("meterFlowVolumeSearchError")
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.entity.meterflowvolume.search.title", "Search Meter Flow Volumes")}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : new Date(),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <MeterFlowVolumeDataTable
                  withBorder
                  records={meterFlowVolumes}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}
                  onEdit={
                    (record) =>
                      navigate("form", {
                        state: {
                          id: record.id,
                        }
                      })
                  }
                  onDelete={
                    (record) =>
                      deleteMeterFlowVolume(record.id, undefined)
                  }/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
